import type { User } from '@/core/auth/types';
import { hasAnyPermission } from '@/core/auth/permissions';

type TreasuryScopeType = 'company' | 'location';

const COMPANY_VIEW_PERMISSIONS = [
  'company.full_access',
  'company.view_treasury',
  'company.manage_treasury',
  'company.manage_treasury_banking',
  'company.manage_treasury_reconciliation',
] as const;

const LOCATION_VIEW_PERMISSIONS = [
  'company.full_access',
  'company.view_treasury',
  'company.manage_treasury',
  'company.manage_treasury_banking',
  'company.manage_treasury_reconciliation',
  'location.view_treasury',
  'location.manage_treasury',
  'location.manage_treasury_banking',
  'location.manage_treasury_reconciliation',
] as const;

const COMPANY_MANAGE_TREASURY_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
] as const;

const LOCATION_MANAGE_TREASURY_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
  'location.manage_treasury',
] as const;

const COMPANY_MANAGE_BANKING_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
  'company.manage_treasury_banking',
] as const;

const LOCATION_MANAGE_BANKING_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
  'company.manage_treasury_banking',
  'location.manage_treasury',
  'location.manage_treasury_banking',
] as const;

const COMPANY_MANAGE_RECONCILIATION_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
  'company.manage_treasury_reconciliation',
] as const;

const LOCATION_MANAGE_RECONCILIATION_PERMISSIONS = [
  'company.full_access',
  'company.manage_treasury',
  'company.manage_treasury_reconciliation',
  'location.manage_treasury',
  'location.manage_treasury_reconciliation',
] as const;

function hasTreasuryPermissions(
  user: User | null | undefined,
  scopeType: TreasuryScopeType,
  companyCodenames: readonly string[],
  locationCodenames: readonly string[],
  locationId?: string | null,
) {
  if (scopeType === 'company') {
    return hasAnyPermission(user, [...companyCodenames], null);
  }
  return hasAnyPermission(user, [...locationCodenames], locationId);
}

export function canViewTreasury(
  user: User | null | undefined,
  scopeType: TreasuryScopeType,
  locationId?: string | null,
) {
  return hasTreasuryPermissions(
    user,
    scopeType,
    COMPANY_VIEW_PERMISSIONS,
    LOCATION_VIEW_PERMISSIONS,
    locationId,
  );
}

export function canManageTreasury(
  user: User | null | undefined,
  scopeType: TreasuryScopeType,
  locationId?: string | null,
) {
  return hasTreasuryPermissions(
    user,
    scopeType,
    COMPANY_MANAGE_TREASURY_PERMISSIONS,
    LOCATION_MANAGE_TREASURY_PERMISSIONS,
    locationId,
  );
}

export function canManageTreasuryBanking(
  user: User | null | undefined,
  scopeType: TreasuryScopeType,
  locationId?: string | null,
) {
  return hasTreasuryPermissions(
    user,
    scopeType,
    COMPANY_MANAGE_BANKING_PERMISSIONS,
    LOCATION_MANAGE_BANKING_PERMISSIONS,
    locationId,
  );
}

export function canManageTreasuryReconciliation(
  user: User | null | undefined,
  scopeType: TreasuryScopeType,
  locationId?: string | null,
) {
  return hasTreasuryPermissions(
    user,
    scopeType,
    COMPANY_MANAGE_RECONCILIATION_PERMISSIONS,
    LOCATION_MANAGE_RECONCILIATION_PERMISSIONS,
    locationId,
  );
}

/**
 * Cash boxes are permissioned per SECTION — commerce, front desk, manager —
 * because whoever counts the bar drawer is not whoever counts the safe.
 *
 * These do NOT imply, and are not implied by, `manage_treasury`. That was the
 * point of splitting them: a receptionist who accepts a drop should not need
 * rights over the whole location's books to do it.
 */
const CASH_SECTION_PERMISSIONS = [
  'company.full_access',
  'location.manage_cash_commerce',
  'location.manage_cash_frontdesk',
  'location.manage_cash_manager',
] as const;

/**
 * Which section owns a box, mirroring `CASH_BOX_SECTION_PERMISSION` on the
 * server. Kept in sync deliberately rather than derived: the two lists
 * disagreeing shows up as a button that opens a dialog and then 403s, which is
 * worse than a button that was never offered.
 */
const CASH_SECTION_BY_SEGMENT: Record<string, string> = {
  pos: 'location.manage_cash_commerce',
  frontdesk: 'location.manage_cash_frontdesk',
  treasury: 'location.manage_cash_manager',
  other: 'location.manage_cash_manager',
};

/**
 * Whether this user may sign for THIS box.
 *
 * Not a re-implementation of the server's decision — it still decides, and it
 * decides last. This is what lets the UI stop offering an action the person
 * cannot take: before it existed, a receptionist was shown "Return it" on a bag
 * from the bar, and found out it was not theirs to return only after typing
 * their password.
 */
export function handlesCashBoxSection(
  user: User | null | undefined,
  segmentUse: string | null | undefined,
  locationId?: string | null,
) {
  const codename = CASH_SECTION_BY_SEGMENT[segmentUse ?? 'other']
    ?? 'location.manage_cash_manager';
  return hasAnyPermission(user, ['company.full_access', codename], locationId);
}

/**
 * Whether this user handles cash in ANY section.
 *
 * The gate for *showing* a cash surface, never for allowing the movement — the
 * server decides that against the specific box's section, and a client copy of
 * that rule would eventually disagree with it.
 */
export function handlesAnyCashBox(
  user: User | null | undefined,
  locationId?: string | null,
) {
  return hasAnyPermission(user, [...CASH_SECTION_PERMISSIONS], locationId);
}
